import { clsx } from "clsx";
import { CalendarIcon } from "lucide-react";
import type { FocusEvent, KeyboardEvent, MouseEvent } from "react";
import { useCallback, useMemo, useRef } from "react";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { useTranslations } from "../../i18n";
import { composeEventHandlers, textFieldButtonSize } from "../../utils";
import { clamp, readInputDate, writeInputDate } from "../../utils/date";
import { IconButton } from "../Button/IconButton";
import { useDropdownContext, useDropdownControlProps } from "../Dropdown";
import type { IInputSize } from "../Input";
import { IMask, Input } from "../Input";
import { useIDatePickerContext } from "./context";
import type { IDatePickerTriggerProps } from "./types";

/**
 * The box positions the panel and takes pointer events. The input holds the
 * `combobox` role; the clear and calendar buttons are its siblings.
 */
export const DatePickerTrigger = ({
  ref,
  className,
  ...anchorProps
}: IDatePickerTriggerProps) => {
  const {
    disabled,
    loading,
    invalid,
    placeholder,
    autoComplete,
    onChange,
    onClear,
    setOpen,
    openCalendar,
    displayValue,
    setDisplayValue,
    inputId: _inputId,
    minDate,
    maxDate,
    ariaLabel: _ariaLabel,
    ariaLabelledby: _ariaLabelledby,
    ariaDescribedby: _ariaDescribedby,
    locale,
    ...contextProps
  } = useIDatePickerContext();
  const isMobile = useMediaQuery("(max-width: 639px)");
  const size: IInputSize = contextProps.size || (isMobile ? "lg" : "md");
  const { t } = useTranslations();
  const { open } = useDropdownContext();
  const controlProps = useDropdownControlProps();
  const inputRef = useRef<HTMLInputElement>(null);

  const parseMaybe = useCallback(
    (text: string) => readInputDate(text, locale.order),
    [locale.order],
  );

  const handleInputChange = useCallback(
    (value: string) => {
      setDisplayValue(value);
      onChange(parseMaybe(value));
    },
    [setDisplayValue, onChange, parseMaybe],
  );

  const handleInputBlur = useCallback(() => {
    const parsedDate = parseMaybe(displayValue);

    if (!parsedDate || (!minDate && !maxDate)) {
      return;
    }

    const clampedDate = clamp(
      parsedDate,
      minDate ?? parsedDate,
      maxDate ?? parsedDate,
    );

    if (clampedDate !== parsedDate) {
      setDisplayValue(writeInputDate(clampedDate, locale.order));
      onChange(clampedDate);
    }
  }, [
    displayValue,
    parseMaybe,
    minDate,
    maxDate,
    setDisplayValue,
    onChange,
    locale.order,
  ]);

  const handleCalendarClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      if (open) {
        setOpen(false);
      } else {
        openCalendar();
      }
    },
    [open, setOpen, openCalendar],
  );

  // Combobox keys: Down (or Alt+Down) opens the calendar with focus on a day.
  const handleInputKeyDown = useCallback(
    (e: KeyboardEvent<HTMLElement>) => {
      if (e.key !== "ArrowDown") return;
      e.preventDefault();
      openCalendar();
    },
    [openCalendar],
  );

  // The box is focusable only so a click on its padding lands somewhere; pass it on.
  const handleBoxFocus = useCallback((e: FocusEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      inputRef.current?.focus();
    }
  }, []);

  const handleClear = onClear
    ? (e?: MouseEvent) => {
        e?.stopPropagation();
        onClear();
      }
    : undefined;

  // The field order follows the format locale: MM/DD/YYYY or DD/MM/YYYY.
  const mask = useMemo(
    () => ({
      mask: locale.pattern,
      blocks: {
        MM: {
          mask: IMask.MaskedRange,
          from: 1,
          to: 12,
          maxLength: 2,
        },
        DD: {
          mask: IMask.MaskedRange,
          from: 1,
          to: 31,
          maxLength: 2,
        },
        YYYY: {
          mask: IMask.MaskedRange,
          from: 1900,
          to: 2100,
          maxLength: 4,
        },
      },
    }),
    [locale.pattern],
  );

  const dataAttributes = Object.fromEntries(
    Object.entries(contextProps).filter(([key]) => key.startsWith("data-")),
  );

  return (
    <div
      {...anchorProps}
      ref={ref}
      className={clsx("nuco-datepicker__trigger", className)}
      onFocus={handleBoxFocus}
    >
      <Input
        {...controlProps}
        ref={inputRef}
        {...dataAttributes}
        role="combobox"
        aria-haspopup="dialog"
        onKeyDown={composeEventHandlers(
          controlProps.onKeyDown,
          handleInputKeyDown,
        )}
        size={size}
        value={displayValue}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        onClear={handleClear}
        disabled={disabled}
        loading={loading}
        invalid={invalid}
        placeholder={placeholder || locale.pattern}
        autoComplete={autoComplete || "off"}
        inputMode="numeric"
        mask={mask}
        endAccessory={
          <IconButton
            size={textFieldButtonSize[size]}
            variant="ghost"
            color="neutral"
            disabled={disabled}
            content={<CalendarIcon aria-hidden="true" />}
            aria-label={
              open
                ? t("ui.datePicker.closeCalendar")
                : t("ui.datePicker.openCalendar")
            }
            aria-expanded={open}
            onClick={handleCalendarClick}
          />
        }
      />
    </div>
  );
};
